import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRequestData } from 'api/api';
import { endpoints } from 'api/endpoints';
import GlobalModal from 'components/globalModal';
import { SingleInvoiceRow } from './singleInvoiceRow';
import { Pagination } from 'antd';
import DynamicRows from './dynamicRows';
import { ROLE_TYPES } from 'api/constants';
import { IconSmallArrow, SelectOption } from 'components';
import { COLORS } from 'utils/colors';

export const InvoiceHistory = ({
  isOpen,
  onClose,
  partnerId,
  role,
  partnerName,
  partnerAddress,
  selectedPartner,
}) => {
  const [invoices, setInvoices] = useState([]);
  const [periodOfInvoices, setPeriodOfInvoices] = useState('weekly');
  const [activeInvoice, setActiveInvoice] = useState(null);

  const chosenPartner = useMemo(() => {
    if (selectedPartner) {
      return selectedPartner;
    }
    return partnerId;
  }, [selectedPartner, partnerId]);

  const handlePaginationChange = async (page) => {
    if (!selectedPartner) {
      const { data } = await getRequestData(
        endpoints.getAllInvoices({
          partnerIds: chosenPartner,
          period: periodOfInvoices,
          page: page,
        }),
        role,
      );
      setInvoices(data);
    } else {
      (async () => {
        const { data } = await getRequestData(
          endpoints.getWeeklyClientInvoices(chosenPartner, periodOfInvoices, page),
          role,
        );
        setInvoices(data);
      })();
    }
  };

  const getInvoiceData = useCallback(
    async (params) => {
      if (!selectedPartner) {
        const { data } = await getRequestData(endpoints.getAllInvoices(params), role);
        setInvoices(data);
      } else {
        const { data } = await getRequestData(
          endpoints.getWeeklyClientInvoices(chosenPartner, periodOfInvoices),
          role,
        );
        setInvoices(data);
      }
    },
    [chosenPartner, periodOfInvoices, role, selectedPartner],
  );

  useEffect(() => {
    getInvoiceData({ period: periodOfInvoices });
  }, [getInvoiceData, periodOfInvoices, selectedPartner]);

  const handlePeriodChange = (period) => {
    setPeriodOfInvoices(period);
  };

  const storeInvoices = useMemo(() => {
    return invoices?.results?.map((invoice) => {
      return (
        <SingleInvoiceRow
          key={invoice.invoice_id}
          invoice={invoice}
          onEditClick={() => setActiveInvoice(invoice)}
        />
      );
    });
  }, [invoices]);

  return (
    <GlobalModal
      open={isOpen}
      width={1044}
      titleLeft
      title={`Invoice History (${periodOfInvoices})`}
      headerSelectBar={
        role === ROLE_TYPES.admin ? (
          <div className="flex w-full gap-x-1">
            <SelectOption
              onSelect={(value) => handlePeriodChange(value)}
              dropdownStyle={{ padding: '16px', background: 'white' }}
              required
              suffixIcon={
                <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
              }
              className="h-[44px] text-base font-medium base-block rounded-lg max-w-[250px]"
              placeholder="Weekly"
              popupClassName="min-w-[160px]"
              defaultValue={periodOfInvoices}
              dropdownRender={(originalNode) => originalNode}
              optionRender={(option) => (
                <div
                  className={`flex items-center gap-x-2 px-4 py-2 m-0 bg-white border rounded-xl ${periodOfInvoices === option.value ? 'border-orange' : 'border-lightGray'}`}
                >
                  <span
                    className={`w-6 h-6 border rounded-full border-orange ${periodOfInvoices === option.value ? 'border-[6px]' : 'border-1'}`}
                  ></span>
                  <p className="font-14 flex-1">{option.label}</p>
                </div>
              )}
              options={[
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' },
              ]}
            />
          </div>
        ) : null
      }
      onClose={onClose}
      partnerName={partnerName}
      partnerAddress={partnerAddress}
    >
      <div className="flex flex-col p-[30px] pt-6 h-full">
        <div className="flex w-full pb-4 border-b border-color-divider">
          <p className="w-[25%] text-14 text-lightBlue">Date</p>
          <p className="w-[22%] text-14 text-lightBlue">SLH</p>
          <p className="w-[11%] text-14 text-lightBlue">Active Residents</p>
          <p className="w-[14%] text-14 text-lightBlue">Invoice Total</p>
          <p className="w-[11%] text-14 text-lightBlue">Status</p>
          <p className="w-[17%] text-14 text-lightBlue text-right">Actions</p>
        </div>
        {activeInvoice ? (
          <div className="flex flex-col">
            <SingleInvoiceRow
              isInEditMode
              invoice={activeInvoice}
              onSubmit={async () => {
                await getInvoiceData({
                  partnerIds: chosenPartner,
                  period: periodOfInvoices,
                  page: invoices.currentPage,
                });
                setActiveInvoice(null);
              }}
            />
            <DynamicRows invoice={activeInvoice} onCancel={() => setActiveInvoice(null)} />
          </div>
        ) : (
          storeInvoices
        )}
        {invoices.pageCount > 1 && !activeInvoice && (
          <Pagination
            className="self-center"
            onChange={(page) => handlePaginationChange(page)}
            current={invoices.currentPage}
            pageCount={invoices.pageCount}
            pageSize={invoices.pageSize}
            showSizeChanger={false}
            total={invoices.count}
          />
        )}
      </div>
    </GlobalModal>
  );
};
